import { DataSourceTypeCode, ReportMode } from '../../Codes';
import * as Requests from './ApiRequestModels';

export class Endpoints {
    // Spotlight
    static DoEvent = '/DoEvent';
    static DoHtmlEvent = '/DoHtmlEvent';
    static Refresh = '/Refresh';
    static Charts = '/Charts';
    static GetMemberRoles = '/GetMemberRoles';
    static SendFeedback = '/SendFeedback';
    static LogEvent = '/LogEvent';
    static Connect = '/Connect';
    static ConnectReport = '/ConnectReport';
    static Report = '/Report';
    static ReportExport = '/ReportExport';
    static UploadAttachment = (windowCode: string, csrfToken: string): string =>
        `/UploadAttachment/${windowCode}?token=${csrfToken}`;

    //Member
    static MemberResetPassword = '/Member/ForceResetPassword';
    static MemberForgotPasswordOidc = '/Member/ForgotPasswordOidc';
    static MemberApiToken = '/Member/ApiToken';
    static ResendMfaCode = '/Member/ResendMfaCode';
    static AuthenticateMemberByMfaCode = '/Member/AuthenticateByMFACode';
    static UpdateMfaSettingsAndAuthenticate = '/Member/UpdateMFASettingsAndAuthenticate';
    static Signup = '/Member/Signup';
    static CreateOrganisation = '/Member/NewOrganisation';

    // Client
    static PreviewPagesClient = '/C/PreviewPages';

    // Report
    static ReportCodes = '/Report/ReportCodes';
    static ReportStatus = '/Report/ReportStatus';
    static ReportVariables = '/Report/ReportVariables';
    static FxRates = '/Report/GetFxRates';
    static AccountSummary = '/Report/AccountSummary';
    static Accounts = '/Report/Accounts';
    static ForecastStatus = '/Report/ForecastStatus';
    static PreviewPages = '/Report/PreviewPages';
    static DataSourceCollection = '/Report/DataSourceCollection';

    static UpdateReport = '/Report/UpdateReport';
    static UpdateCharts = '/Report/Charts';
    static UpdateDashPanel = '/Report/UpdateDashPanel';
    static UpdateFxRates = `/Report/SaveFxRates`;
    static KpiPreview = '/Report/KPIPreview';
    static GetOrCreateReportBuildStatus = (
        windowCode: string,
        reportId: string,
        triggerNewIfFailed: boolean,
        pageId?: string,
    ): string =>
        `/Report/getorcreatereportbuildstatus/${windowCode}?reportId=${reportId}&triggerNewIfFailed=${triggerNewIfFailed}` +
        (pageId ? `&pageId=${pageId}` : '');

    // Report history
    static Reports = '/ReportHistory/GetReports';
    static RecentReports = '/ReportHistory/GetRecentReports';

    // Report export
    static StartExport = '/ReportExport/StartExport';
    static DocumentStatus = '/ReportExport/DocumentStatus';

    // Forecast
    static ForecastTaskManager = '/Forecast/TaskManager';
    static ForecastJournals = '/Forecast/Journals';
    static ValidateDynamicRule = '/Forecast/ValidateDynamicRule';
    static ValidateWagesRule = '/Forecast/ValidateWagesRule';
    static ValidateBalanceSheetRule = '/Forecast/ValidateBalanceSheetRule';
    static GetCyclicalReferences = '/Forecast/GetCyclicalReferences';
    static RunRulesForDataSourcesUsingDrivers = '/Forecast/RunRulesForDataSourcesUsingDrivers';
    static ValidateMeasure = '/Forecast/ValidateMeasure';
    static CalculateLoanAmortisation = '/Forecast/CalculateLoanAmortisation';
    static ExportLoanAmortisation = '/Forecast/ExportLoanAmortisation';

    // FormulaBuilder
    static FormulaGallery = '/FormulaGallery/List';
    static CreateFormula = '/FormulaGallery/CreateFormula';
    static CheckFormulaName = '/FormulaGallery/CheckFormulaName';
    static SetTags = '/FormulaGallery/SetTags';
    static SaveFormula = '/FormulaGallery/SaveFormula';
    static DeleteCustomFormula = '/FormulaGallery/DeleteCustomFormula';

    // Practice
    static PracticeThumnails = '/Practice/Thumbnails';
    static CoverPreview = '/Practice/CoverPreview';
    static CoverTemplates = '/Practice/CoverTemplates';
    static ColorPalette = '/Practice/ColorPalette';
    static GetPasswordHistoryPreference = '/Practice/GetPasswordHistoryPreference';
    static UpdatePasswordHistoryPreference = '/Practice/UpdatePasswordHistoryPreference';
    static GetPasswordExpiryPreference = '/Practice/GetPasswordExpiryPreference';
    static UpdatePasswordExpiryPreference = '/Practice/UpdatePasswordExpiryPreference';

    // Organisation
    static CheckOrganisationName = '/Organisation/CheckName';
    static CheckGroupName = '/Organisation/CheckGroupName';
    static GetFilteredOrganisationNames = '/Organisation/Names';
    static GetFilteredLocations = '/organisation/locations';

    // DataSource
    static SaveFilePath = '/DataSource/SaveFilePath';
    static GetUploadUrl = '/DataSource/GetUploadUrl';
    static SetOptionsValue = '/DataSource/SetOptionsValue';
    static DisconnectDataSource = '/DataSource/Disconnect';
    static ManualPersonalWealth = '/Datasource/ManualPersonalWealth';
    static NewDataSource = '/DataSource/New';
    static CheckGoogleAccess = '/DataSource/CheckGoogleAccess';

    static ConnectFranchiseeDataSource = ({
        dataSourceTypeCode,
        windowCode,
        settings,
    }: {
        dataSourceTypeCode: DataSourceTypeCode;
        windowCode?: string;
        settings: {
            reportMode: string;
            franchiseId: string;
            actionCode: string;
            name?: string;
            abbreviation?: string;
        };
    }): string => {
        const supportedDataSourceTypes = [
            DataSourceTypeCode.MyobAccountRight,
            DataSourceTypeCode.QuickBooks,
            DataSourceTypeCode.Xero,
        ];

        if (!supportedDataSourceTypes.includes(dataSourceTypeCode)) {
            throw new Error('Data source type not supported');
        }

        const baseUrl = `/datasource/${dataSourceTypeCode.toLowerCase()}franchisee/${windowCode}`;
        const queryStringParams = new URLSearchParams(settings);

        return `${baseUrl}?${queryStringParams}`;
    };

    // Desktop
    static GenerateWebToken = '/Desktop/GenerateWebToken';

    // Integrations
    static CompanyFileLinked = 'IsCompanyFileLinked';
    static OpenCompanyFile = 'OpenCompanyFile';
    static SelectCompanyFile = 'SelectCompanyFile';

    // MYOB
    static OpenMyobCompanyFile = '/MYOB/OpenCompanyFile';
    static IsMyobCompanyFileLinked = '/MYOB/IsCompanyFileLinked';

    // Mailgun
    static CheckSendingStatus = '/Mailgun/CheckSendingStatus';
    static ValidateEmail = '/Mailgun/ValidateEmail';

    // Desktop connector
    static AgentIsRunning = 'AgentIsRunning';
    static OpenFile = 'OpenFile';
    static FileStatus = 'FileStatus';
    static JobStatus = 'Import/Status';
    static CancelJob = 'Import/Cancel';
    static ImportClasses = 'Classes';
    static UploadDataFile = 'UploadDataFile';

    // Notifications
    static GetNotifications = '/Notification/GetNotifications';
    static NotificationBatchUpdate = '/Notification/BatchUpdate';
    static NotificationUpdate = '/Notification/Update';
    static NotificationDelete = '/Notification/Delete';
    static NotificationDeleteAll = '/Notification/DeleteAll';
}

export class WebApiEndpoints {
    // Spotlight
    static GetCurrencies = '/webapi/currencies';

    // Member
    static GetMFASettings = '/webapi/members/mfasettings';
    static GetMFASettingsForMember = (memberId: string): string => `/webapi/members/${memberId}/mfasettings`;
    static VerifyMFASettingsForMember = (memberId: string): string => `/webapi/members/${memberId}/mfasettings/verify`;
    static UpdateReleaseNotificationReadDate = '/webapi/members/updatereleasenotificationreaddate';
    static GetLoginOIDCType = '/webapi/members/loginoidctype';
    static GetProductAccess = '/webapi/members/productaccess';

    // Notifications
    static GetUnreadNotificationCount = '/webapi/notification/getUnreadCount';

    // Organisation
    static GetOrganisationTypeCode = ({ organisationId }: Requests.GetOrganisationTypeCodeRequestRoute): string =>
        `/webapi/organisation/${organisationId}/TypeCode`;
    static GetOrganisationsForSupport = '/webapi/organisation/organisationsforsupport';
    static OrganisationTrackingSettings = (organisationId: string): string =>
        `/webapi/organisation/${organisationId}/trackingsettings`;
    static GetThumbnails = (reportId: string, thumbnailId: string, pages: number): string =>
        `/webapi/reports/${reportId}/thumbnails/${thumbnailId}?pages=${pages}`;
    static LoadThumbnails = (reportId: string, localTime: string): string =>
        `/webapi/reports/${reportId}/loadthumbnails?localTime=${localTime}`;
    static GetGrid = (
        accountCode: string,
        reportId: string,
        dataSourceId: string,
        mode: string,
        organisationId: string,
        date: string,
        tracking1: string,
        tracking2: string,
    ): string =>
        `/webapi/organisation/${organisationId}/report/${reportId}/griddata?accountCode=${accountCode}&dataSourceId=${dataSourceId}&mode=${mode}&date=${date}${tracking1}${tracking2}`;
    static GetPreviewPage = (
        windowCode: string,
        reportId: string,
        pageId: string,
        timeZoneOffset: number,
        localTime: string,
        activeChartId: string,
    ): string =>
        `/Report/PagePreview/${windowCode}?reportid=${reportId}&pageId=${pageId}&timeZoneOffset=${timeZoneOffset}&localTime=${localTime}&activeChartId=${activeChartId}`;
    static GetAllPreviewPages = (
        windowCode: string,
        reportId: string,
        timeZoneOffset: number,
        localTime: string,
    ): string =>
        `/Report/AllPagesPreview/${windowCode}?reportid=${reportId}&timeZoneOffset=${timeZoneOffset}&localTime=${localTime}`;

    static GetAllPreviewPagesFromCache = (
        windowCode: string,
        reportId: string,
        cacheId: string,
        timeZoneOffset: number,
        localTime: string,
    ): string =>
        `/Report/AllPagesPreviewFromCache/${windowCode}?reportid=${reportId}&cacheId=${cacheId}&timeZoneOffset=${timeZoneOffset}&localTime=${localTime}`;

    static GetOrganisationUsage = (organisationId: string, reportMode: string): string =>
        `/webapi/organisation/${organisationId}/usage?reportMode=${reportMode}`;
    static GetOrganisationDataSourceCollection = (organisationId: string, reportMode: string): string =>
        `/webapi/organisation/${organisationId}/externaldatasources?reportMode=${reportMode}`;
    static GetOrganisationCountryCode = (organisationId: string): string =>
        `/webapi/organisation/${organisationId}/countrycode`;
    static ValidateOrganisationNameIsUnique = (reportmode: ReportMode, name: string): string =>
        `/webapi/organisation/${reportmode}/NameIsUnique?name=${name}`;
    static DeleteOrganisation = (organisationId: string, ignoreDashboard: boolean): string =>
        `/webapi/organisation/${organisationId}${ignoreDashboard ? '?ignoreDashboard=true' : ''}`;
    static GetOrCreateMemberWindow = (organisationId: string): string =>
        `/webapi/organisation/${organisationId}/MemberWindow`;
    static GetOrganisationCountLimits = (reportMode: ReportMode): string =>
        `/webapi/organisation/${reportMode}/GetOrgCountLimits`;
    static GetOrganisationCountLimitsFranchise = (reportMode: ReportMode, franchiseId: string): string =>
        `/webapi/organisation/${reportMode}/GetOrgCountLimits?franchiseId=${franchiseId}`;
    static GetOrganisations = (product: string): string => `/webapi/organisation/${product}/landingpage`;
    static GetGroupedOrganisationIDs = (groupId: string): string =>
        `/webapi/organisation/${groupId}/GroupedOrganisationIDs`;
    static GetForecastingOrganisations = '/webapi/organisation/forecastinglandingpage';
    static GetMultiHeadOffices = '/webapi/organisation/multilandingpage';
    static GetOrganisationClients = (organisationId: string): string =>
        `/webapi/organisation/${organisationId}/clients`;
    static GetFractionalOwnershipSettings = (groupId: string): string =>
        `/webapi/organisation/${groupId}/fractionalownership`;
    static UpdateFractionalOwnershipRecord = (groupId: string, organisationId: string): string =>
        `/webapi/organisation/${groupId}/fractionalownership/${organisationId}`;
    static DeleteFractionalOwnershipRecord = (groupId: string, organisationId: string, date: string): string =>
        `/webapi/organisation/${groupId}/fractionalownership/${organisationId}?dateAcquired=${date}`;

    // Accounts
    static GetDataSourceAccountsByReportCode = (
        organisationId: string,
        dataSourceId: string,
        reportCode: string,
    ): string => `/webapi/Organisation/${organisationId}/DataSource/${dataSourceId}?reportCode=${reportCode}`;

    static GetAccountDataByAccountId = (
        organisationId: string,
        accountId: string,
        fromDate: string,
        toDate: string,
    ): string =>
        `/webapi/organisation/${organisationId}/Account/${accountId}/Data?fromDate=${fromDate}&toDate=${toDate}`;

    static GetAccount = ({ organisationId, accountId }: Requests.GetAccountRequestRoute): string =>
        `/webapi/organisation/${organisationId}/accounts/${accountId}`;
    static GetAccountsDistinct = (organisationId: string, reportMode: string): string =>
        `/webapi/organisation/${organisationId}/AccountsDistinct?reportMode=${reportMode}`;
    static UpdateAccountDisplayName = ({
        organisationId,
        accountId,
    }: Requests.UpdateAccountDisplayNameRequestRoute): string =>
        `/webapi/organisation/${organisationId}/accounts/${accountId}/DisplayName`;
    static CreateSustainAccount = (organisationId: string, dataSourceId: string): string =>
        `/webapi/organisation/${organisationId}/datasource/${dataSourceId}/SustainAccount`;
    static UpdateSustainAccount = (organisationId: string, accountId: string): string =>
        `/webapi/organisation/${organisationId}/SustainAccount/${accountId}`;
    static DeleteSustainAccounts = (organisationId: string): string =>
        `/webapi/organisation/${organisationId}/SustainAccounts`;

    // Forecasting
    static GetForecastStatus = (dataSourceId: string): string => `/webapi/forecast/${dataSourceId}/status`;
    static GetForecastSettings = (reportId: string, dataSourceId: string): string =>
        `/webapi/forecast/${reportId}/task/${dataSourceId}/settings`;
    static GetDefaultForecastSettings = (reportId: string): string => `/webapi/forecast/${reportId}/DefaultSettings`;
    static GetDefaultForecastSettingsDates = (organisationId: string): string =>
        `/webapi/forecast/${organisationId}/DefaultSettingsDates`;
    static GetForecastTaskSyncSettings = (taskId: string): string => `/webapi/forecast/${taskId}/TaskSyncSettings`;
    static GetForecastGridData = (
        reportId: string,
        dataSourceId: string,
        accountCode: string,
        mode: string,
        startDate: string,
        endDate: string,
        tracking1: string,
        tracking2: string,
    ): string =>
        `/webapi/forecast/${reportId}/task/${dataSourceId}/griddata?accountCode=${accountCode}&mode=${mode}&startDate=${startDate}&endDate=${endDate}${tracking1}${tracking2}`;
    static GetForecastMeasuresGridData = (organisationId: string, startDate: string, endDate: string): string =>
        `/webapi/forecast/${organisationId}/measuresgriddata?startDate=${startDate}&endDate=${endDate}`;
    static GetForecastGroupGridData = ({
        organisationId,
        bandType,
        startDate,
        endDate,
        reportCode,
    }: Requests.ForecastGroupGridDataRequest): string => {
        let path = `/webapi/forecast/group/${organisationId}/griddata?bandType=${bandType}&startDate=${startDate}&endDate=${endDate}`;
        if (typeof reportCode !== 'undefined') {
            path += `&reportCode=${reportCode}`;
        }
        return path;
    };
    static GetForecastDataSourceUsage = (dataSourceId: string, excludeDataSourceId: string): string =>
        `/webapi/forecast/${dataSourceId}/usage?excludeDataSourceId=${excludeDataSourceId}`;
    static GetForecastOrganisations = (): string => `/webapi/forecast/organisations`;
    static GetForecastOrganisationTasks = (organisationId: string): string =>
        `/webapi/forecast/${organisationId}/tasks`;
    static GetForecastTasks = (organisationId: string): string => `/webapi/forecast/${organisationId}/forecasttasks`;
    static CreateForecastTask = (organisationId: Requests.CreateForecastTaskRequestRoute['organisationId']): string =>
        `/webapi/forecast/task/${organisationId}`;
    static CopyForecastTask = (dataSourceId: string): string => `/webapi/forecast/task/${dataSourceId}/copy`;
    static GetForecastTaskSettings = (taskId: string): string => `/webapi/forecast/${taskId}/tasksettings`;
    static GetForecastTaskSummary = (taskId: string): string => `/webapi/forecast/task/${taskId}/summary`;
    static GetForecastDriversId = (organisationId: string): string => `/webapi/forecast/${organisationId}/driversid`;
    static GetForecastMeasuresId = (organisationId: string): string => `/webapi/forecast/${organisationId}/measuresid`;
    static GetForecastDriversMeasuresSummary = (dataSourceId: string): string =>
        `/webapi/forecast/driversmeasures/${dataSourceId}/summary`;
    static GetForecastGroupSettings = (organisationId: string): string =>
        `/webapi/forecast/group/${organisationId}/settings`;
    static GetForecastGroupTasks = (organisationId: string): string => `/webapi/forecast/group/${organisationId}/tasks`;
    static ValidateForecastRules = (dataSourceId: string): string => `/webapi/forecast/${dataSourceId}/validaterules`;
    static CreateForecastGroup = (): string => `/webapi/forecast/newgroup`;
    static UpdateForecastTaskSettings = (taskId: string): string => `/webapi/forecast/task/${taskId}/settings`;
    static UpdateForecastDriversMeasuresSettings = (dataSourceId: string): string =>
        `/webapi/forecast/driversmeasures/${dataSourceId}/settings`;
    static SyncForecastTaskData = (organisationId: string, taskId: string): string =>
        `/webapi/forecast/${organisationId}/task/${taskId}/syncdata`;
    static UpdateForecastAccountDatum = (
        dataSourceId: string,
        accountId: string,
        date: string,
        metric: string,
        value: number,
        tracking1: string,
        tracking2: string,
    ): string =>
        `/webapi/forecast/task/${dataSourceId}/account/${accountId}/datum?date=${date}&metric=${metric}&value=${value}${tracking1}${tracking2}`;
    static UpdateForecastGroupSettings = (organisationId: string): string =>
        `/webapi/forecast/group/${organisationId}/settings`;
    static AddForecastGroupTask = (organisationId: string): string => `/webapi/forecast/group/${organisationId}/tasks`;
    static RefreshForecastGroupTaskData = (organisationId: string, taskDataSourceId: string): string =>
        `/webapi/forecast/group/${organisationId}/task/${taskDataSourceId}/sync`;
    static DeleteForecastTaskDataSource = (dataSourceId: string): string => `/webapi/forecast/task/${dataSourceId}`;
    static DeleteForecastGroupTask = (organisationId: string, taskDataSourceId: string): string =>
        `/webapi/forecast/group/${organisationId}/task/${taskDataSourceId}`;
    static GetForecastSyncStatus = (organisationId: string): string =>
        `/webapi/forecast/group/${organisationId}/syncstatus`;
    static ResetForecastGroupTemplate = (reportId: string): string =>
        `/webapi/forecast/group/${reportId}/resettemplate`;
    static GetForecastEliminations = (organisationId: string): string =>
        `/webapi/forecast/group/${organisationId}/eliminations`;
    static UpdateForecastEliminations = ({
        organisationId,
        section,
    }: {
        organisationId: string;
        section: string;
    }): string => `/webapi/forecast/group/${organisationId}/eliminations?sectionCode=${section}`;
    static GetForecastEliminationAdjustments = (organisationId: string): string =>
        `/webapi/forecast/group/${organisationId}/eliminationadjustments`;
    static UpdateForecastEliminationAdjustments = (organisationId: string): string =>
        `/webapi/forecast/group/${organisationId}/eliminationadjustments`;
    static GetForecastEliminationsStatus = (organisationId: string): string =>
        `/webapi/forecast/group/${organisationId}/eliminationsstatus`;
    static GetForecastEliminationTab = (organisationId: string): string =>
        `/webapi/forecast/group/${organisationId}/EliminationTab/`;
    static GetForecastShowNoJournalWarning = (organisationId: string): string =>
        `/webapi/forecast/group/${organisationId}/ShowNoJournalWarning`;

    static ValidateForecastTaskDescriptionUnique = (organisationId: string, description: string): string => {
        return `/webapi/forecast/${organisationId}/validatetaskdescriptionunique?description=${encodeURIComponent(
            description,
        )}`;
    };
    static ValidateForecastAccountDeletion = (organisationId: string, accountId: string): string =>
        `/webapi/forecast/${organisationId}/validateaccountdeletion?accountId=${accountId}`;
    static DeleteForecastAccount = (organisationId: string, accountId: string): string =>
        `/webapi/forecast/${organisationId}/account?accountId=${accountId}`;
    static GetForecastIncomeTaxRule = (organisationId: string, ruleId: string): string =>
        `/webapi/forecast/${organisationId}/Task/Rule?ruleId=${ruleId}`;
    static UpdateForecastRule = (organisationId: string, dataSourceId: string, ruleId: string): string =>
        `/webapi/forecast/${organisationId}/Task/${dataSourceId}/Rule?ruleId=${ruleId}`;
    static GetJournalAccounts = (organisationId: string, dataSourceId: string): string =>
        `/webapi/forecast/${organisationId}/journalaccounts?dataSourceId=${dataSourceId}`;
    static GetDefaultPaymentProfiles = (organisationId: string): string =>
        `/webapi/forecast/${organisationId}/defaultpaymentprofiles`;

    // Page
    static GetPageGallery = ({ organisationType, reportMode }: Requests.GetPageGalleryRequestRoute): string =>
        `/webapi/page/metadata/?organisationTypeCode=${organisationType}&reportMode=${reportMode}`;

    // Reports
    static GetActiveReportPages = (reportId: Requests.GetActiveReportPagesRequestRoute['reportId']): string =>
        `/webapi/reports/${reportId}/ActivePages`;
    static AddPageToReport = (reportId: Requests.AddPageRequestRoute['reportId']): string =>
        `/webapi/reports/${reportId}/NewReportPage`;
    static AddChartToReport = (
        reportId: Requests.AddChartRequestRoute['reportId'],
        pageId: Requests.AddChartRequestRoute['pageId'],
        sectionId: Requests.AddChartRequestRoute['sectionId'],
    ): string => `/webapi/reports/${reportId}/Pages/${pageId}/Sections/${sectionId}/NewReportChart`;
    static GetReportPageSettings = (reportId: string, pageId: string): string =>
        `/webapi/reports/${reportId}/pages/${pageId}/settings`;
    static UpdateReportPageSettings = (reportId: string, pageId: string): string =>
        `/webapi/reports/${reportId}/pages/${pageId}/settings`;
    static GetPageSettingsOptions = (reportId: string, pageId: string, settingExpression: string): string =>
        `/webapi/reports/${reportId}/pages/${pageId}/settings/${settingExpression}/options`;
    static SetTrackingReportFilter = (reportId: string): string => `/webapi/reports/${reportId}/trackingFilter`;
    static GetTrackingReportFilter = (reportId: string): string => `/webapi/reports/${reportId}/trackingFilter`;
    static GetTrackingReportPageFilter = (reportId: string, pageId: string): string =>
        `/webapi/reports/${reportId}/PageTrackingFilter/${pageId}`;
    static GetTrackingReportPagesFilter = (reportId: string): string =>
        `/webapi/reports/${reportId}/PageTrackingFilter`;
    static SetTrackingReportPageFilter = (reportId: string): string => `/webapi/reports/${reportId}/PageTrackingFilter`;

    static GetReportColorPalette = (reportId: string): string => `/webapi/reports/${reportId}/colorpalette`;
    static GetReportNavigation = (reportId: string): string => `/webapi/reports/${reportId}/navigation`;
    static GetReportContext = (reportId: string): string => `/webapi/reports/${reportId}/context`;

    static GetDataSourceStatus = (dataSourceId: string): string => `/webapi/datasource/${dataSourceId}/status`;

    static DisconnectDataSource = (dataSourceId: string): string => `/webapi/datasource/${dataSourceId}/disconnect`;

    static DataSourceTrackingConfigured = (dataSourceId: string): string =>
        `/webapi/DataSource/${dataSourceId}/TrackingConfigured`;

    static DeleteDataSource = (dataSourceId: string): string => `/webapi/datasource/${dataSourceId}`;

    static GetTextPanelContent = (reportId: string, pageId: string, chartId: string): string =>
        `/webapi/reports/${reportId}/pages/${pageId}/textPanel/${chartId}`;

    static UpdateTextPanelContent = (reportId: string, pageId: string, chartId: string): string =>
        `/webapi/reports/${reportId}/pages/${pageId}/textPanel/${chartId}`;

    static CreateNewSustainReport = (organisationId: string, useRecentReport: boolean): string =>
        `/webapi/reports/NewSustain/${organisationId}?useRecentReport=${useRecentReport}`;

    static SelectedBudgets = (reportId: string): string => `/webapi/reports/${reportId}/selectedbudgets`;

    // Xero Importing
    static GetAuthorisedXeroCompanies = (dataSourceId: string): string =>
        `/webapi/datasource/${dataSourceId}/GetAuthorisedXeroCompanies`;
    static SetAuthorisedXeroCompany = (dataSourceId: string): string =>
        `/webapi/datasource/${dataSourceId}/SetSelectedCompany`;

    // Sage Intacct Importing
    static SetupSageIntacct = (dataSourceId: string): string => `/webapi/datasource/${dataSourceId}/SetupSageIntacct`;
    static SelectSageIntacctEntity = (dataSourceId: string): string =>
        `/webapi/datasource/${dataSourceId}/SelectSageIntacctEntity`;
    static GetSageIntacctDimensions = (dataSourceId: string): string =>
        `/webapi/datasource/${dataSourceId}/GetSageIntacctDimensions`;
    static SetTrackingCategories = (dataSourceId: string): string =>
        `/webapi/datasource/${dataSourceId}/SetTrackingCategories`;

    // MYOB Importing
    static GetMyobCompanyFiles = (dataSourceId: string): string =>
        `/webapi/datasource/${dataSourceId}/myobcompanyfiles`;

    // Google Sheets Importing
    static GoogleSpreadsheet = (dataSourceId: string): string => `/webapi/datasource/${dataSourceId}/googlespreadsheet`;

    // QuickBooks
    static GetQboDataSources = (realmId: string): string => `/webapi/datasource/${realmId}/qbodatasources`;

    //Spotlight Forecast
    static GetForecastingTasks = (dataSourceId: string): string =>
        `/webapi/datasource/${dataSourceId}/GetForecastingTasks`;
    static SetForecastingTasks = (dataSourceId: string): string =>
        `/webapi/datasource/${dataSourceId}/SetForecastingTask`;

    // Feature Flags
    static DefaultFeatureFlags = (): string => `/webapi/featureflags`;
    static PracticeFeatureFlags = (): string => `/webapi/practice/featureflags`;
    static PracticeName = (): string => `/webapi/practice/name`;
    static GetFeatureFlagValue = (featureFlag: string): string => `/webapi/practice/GetFeatureFlagValue/${featureFlag}`;
    static GetAllFeatureFlagValues = '/webapi/practice/GetAllFeatureFlagValues';

    // ReportHistory
    static GetRecentReports = (): string => `/webapi/reporthistory/RecentReports`;

    // Tracking
    static CategoryAbbreviations = (dataSourceId: string): string =>
        `/webapi/datasource/${dataSourceId}/Tracking/CategoryAbbreviations`;

    // Account Groupings
    static CreateAccountGroupingLayout = (organisationId: string): string =>
        `/webapi/organisation/${organisationId}/accountgroupings`;

    static DeleteAccountGroupingLayout = (
        organisationId: string,
        layoutId: string,
        reportId: string,
        pageId: string,
    ): string =>
        `/webapi/organisation/${organisationId}/accountgroupings/${layoutId}?reportId=${reportId}&pageId=${pageId}`;

    static GetAccountGroupingLayout = (organisationId: string, layoutId: string): string =>
        `/webapi/organisation/${organisationId}/accountgroupings/${layoutId}`;

    static GetAllAccountGroupingLayouts = (organisationId: string, pageType: string): string =>
        `/webapi/organisation/${organisationId}/accountgroupings?pageType=${pageType}`;

    static SetAccountGroupingLayout = (organisationId: string): string =>
        `/webapi/organisation/${organisationId}/accountgroupings`;

    static SetPageAccountGroupingLayout = (reportId: string, pageId: string, layoutId: string): string =>
        `/webapi/reports/${reportId}/pages/${pageId}/accountgroupings/${layoutId}`;

    static UpdateAccountGroupingPageOptions = (reportId: string, pageId: string): string =>
        `/webapi/reports/${reportId}/pages/${pageId}/accountgroupings/options`;

    // Data Import
    static ImportDataSource = (dataSourceId: string, reportMode: string): string =>
        `/webapi/datasource/${dataSourceId}/importdatasource/${reportMode}`;

    static SetUpBulkImport = (groupOrganisationId: string): string =>
        `/webapi/datasource/${groupOrganisationId}/setupbulkimport`;

    static HandleInProgressBulkImport = (): string => `/webapi/datasource/handleinprogressbulkimport`;

    static TriggerStuckBulkImport = (dataSourceId: string): string =>
        `/webapi/datasource/${dataSourceId}/triggerstuckbulkimport`;

    static DefaultImportSettings = (dataSourceId: string, reportMode: string): string =>
        `/webapi/datasource/${dataSourceId}/defaultimportsettings/${reportMode}`;

    // Configuration
    static SpotlightConfig = (): string => `/webapi/config`;

    // Payment Schedules
    static GetPracticePaymentSchedules = (countryCode: string): string =>
        `/webapi/practice/paymentschedules/${countryCode}`;

    // Billing
    static GetFranchiseOrganisations = (): string => '/webapi/billing/getfranchiseorganisations';

    static GetCoreOrganisations = (): string => 'webapi/billing/getcoreorganisations';

    static GetOrganisationsByProduct = (productCode: string): string =>
        `webapi/billing/getorganisationsbyproduct/${productCode}`;

    static GetPricingPlans = (currentPlanCode: string): string => `/webapi/billing/pricing/${currentPlanCode}`;

    static GetSubscriptionDetails = (): string => `/webapi/billing/subscriptiondetails`;

    static GetNonAdminBillingData = (): string => `/webapi/billing/nonadminbillingdata`;

    static RetryPayment = (productCode: string): string => `/webapi/billing/retrypayment/${productCode}`;

    static Subscribe = (
        featuresToPlanCodes: string,
        cardToken: string,
        firstName: string,
        lastName: string,
        email: string,
        postcode: string,
        billingFrequency?: string,
    ): string =>
        `/webapi/billing/subscribe?featuresToPlanCodes=${featuresToPlanCodes}&cardToken=${cardToken}&firstName=${firstName}&lastName=${lastName}&email=${email}&postcode=${postcode}&billingFrequency=${billingFrequency}`;

    static StartTrial = (productCode: string, planCode: string): string =>
        `/webapi/billing/starttrial?productCode=${productCode}&planCode=${planCode}`;

    static GetMemberIdentifiers = (): string => `/webapi/members/getidentifiers`;

    static GetLocations = '/webapi/members/LocationOptions';

    static IsEmailInUse = (email: string): string => `/webapi/billing/isemailinuse?email=${email}`;

    // Sustain
    static CreateSustainOrganisation = '/webapi/organisation/NewSustain';
    static CreateSustainTemplate = (reportId: string): string => `/webapi/reports/${reportId}/sustaintemplate`;
    static UploadImportFile = (windowCode: string): string => `/UploadImportFile/${windowCode}`;
    static UpdateReportSetupStep = (reportId: string, setupStep: string): string =>
        `/webapi/reports/${reportId}/reportsetupstep?setupStep=${setupStep}`;

    // Karbon
    // used for: DELETE, POST and GET/check
    static KarbonConnection = '/webapi/karbon/Connection';

    static CreateKarbonReport = '/webapi/karbon/Report/Create';
    static RevertCreateKarbonReport = '/webapi/karbon/Report/Revert';
    static UpdateKarbonIntegrationTaskStatus = '/webapi/karbon/Report/Update';
    static UploadReportToKarbon = '/webapi/karbon/Report/Upload';

    static CreateReportIntegrationTaskLink = '/webapi/karbon/Link/Report/IntegrationTask';
    static CreateOrganisationClientLink = '/webapi/karbon/Link/Organisation/Client';
    static GetKarbonLinksBuildStatus = (karbonLinksBuildRecordId: string): string =>
        `/webapi/karbon/Link/Build/Status/${karbonLinksBuildRecordId}`;

    static GetClientOrganisationLink = (reportId: string): string => `/webapi/karbon/Client/Org/${reportId}`;
    static GetClientUnlinkedIntegrationTasks = (clientKey: string, clientType: string, reportMode: string): string =>
        `/webapi/karbon/Client/IntegrationTask/${clientKey}/${clientType}/${reportMode}`;

    // Action plan
    static NewActionPlanPage = (reportId: string): string => `/webapi/reports/${reportId}/NewActionPlanPage`;
}
